/**
 * «Asignación inicial» — the files (CSV and Excel) of the app's pages (bata-platform#939 §8, `docs/adr/5001`).
 *
 * Every page that draws a table registers its file (R20): the articles to allocate (a market's list), and the run's
 * workbook behind the three run pages. The workbook is ONE per run — the run's articles, the hold-back scenarios, the
 * lines, the cells report and the parameters in force — and the CSV is its first sheet's table, the one the page the planner
 * downloaded it from draws: the lines page opens on the lines, the runs page on the articles. **The approvals page's file is another:**
 * the lines somebody approved or adjusted (and no other), then the parameters in force — what leaves the app once the decisions are taken.
 *
 * **The rows are the platform's own files**, each list in ONE streamed request (`GET …/export?kind=`, `./csv.ts`), the sheets
 * and their order from the platform's workbook description (`GET …/workbook`), read when a button is pressed — never on load
 * (ADR 9076 point 3). The CSV reads only the list it holds. Stores and articles stand beside their codes by name (`./names.tsx`),
 * read for the keys the file holds.
 *
 * **A file is read by a person.** A yes or a no is a word in the workbook (the CSV keeps `true` / `false`); a decision, its reason and its
 * decider are words and a name (ADR 9026, 9041); a ship week is the Monday it starts on; a size is never its key; a parameter's value is in
 * the unit the planner reads it in. An empty cell that means «does not apply» is blank, never «Non assortito».
 *
 * Nothing is invented: a field the platform does not state is a missing cell, and a line's two facts the client asked the file to
 * carry — the day it stops holding pairs, and whether its store's shipment stays under the minimum (DEC-127: flagged, never
 * dropped) — are columns of their own, each with the platform's reason where it states none.
 *
 * v1 stops here: the file is the end of the road. Nothing is written back to Bata's systems.
 */

import { callOperation, collectPages, NO_REASON_LABELS, readReasonLabels, type CsvProgress } from '@/api';
import {
  dispositionReasonCodes, launchListRuns, launchReadReasonLabels, launchReadRun, listArticlesToAllocate, launchReadLineWhy, listRunCells, readArticleScenarios, readRunWorkbook,
  type ArticleToAllocate, type RunArticle, type RunCell, type RunScenario, type RunSummary, type RunView, type SimilarArticleShare,
} from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, ExportTranslate, ProvenanceFilter, WorkbookSheet } from '@/export';
import { i18next } from '@/i18n';
import { formatPercent } from '@/lib/format';
import { PERSON_NAME_DISPLAY, SIZE_EXPORT_COLUMNS, decidedByExportCell, readRowParam, sizeAbsentReasonKey, sizeExportCells, sizeName } from '@/planning';

import type { ExportScope } from '../../manifest';
import { COLUMN_KEYS } from './file-columns';
import { ARTICLES_LIST, CELLS_LIST, LINES_LIST, SCENARIOS_LIST, readList, type LineDisposition, type LineRow } from './csv';
import { NO_NAMES, readNames, type Named, type Names } from './names';
import { paramName, parameterValueWords, scopeWords } from './parameters';
import { reasonWordsFrom, type ReasonWords } from './reasons';
import { readCells, readExcluded, readMarket, readNotAllocable, readOpenArticle, readRunId } from './scope';
import {
  BOOLEAN_KEY, CELL_OUTCOME_KEY, DISPOSITION_KEY, OUTCOME_KEY, PRICE_BASIS_KEY,
  SIMILARITY_BASIS_KEY, isoWeekStart, translatorOf, type Table,
} from './words';

// ── cells ────────────────────────────────────────────────────────────────────

const MISSING: ExportCell = { kind: 'missing', state: 'not-reported' };
/** An empty cell that means «does not apply here». */
const NOT_APPLICABLE: ExportCell = { kind: 'missing', state: 'not-ranged' };

const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? MISSING : { kind: 'text', value });
const label = (code: string | null | undefined): ExportCell => (code === null || code === undefined || code === '' ? NOT_APPLICABLE : { kind: 'label', value: code, code });
const integer = (value: number | null | undefined): ExportCell => (value === null || value === undefined ? MISSING : { kind: 'integer', raw: value });
const date = (iso: string | null | undefined): ExportCell => (iso === null || iso === undefined || iso === '' ? MISSING : { kind: 'date', iso });
/** A yes or a no: the workbook says it in words (`YES_NO`), the CSV keeps `true` / `false`. */
const yesNo = (value: boolean | null | undefined): ExportCell => (value === null || value === undefined ? MISSING : { kind: 'label', value: String(value), code: String(value) });
const named = (one: Named): ExportCell => (one.name === null ? MISSING : { kind: 'text', value: one.name });
/** «Name (code)» for a key inside a sentence-like cell; the code alone where nothing names it. */
const nameWithCode = (translate: ExportTranslate, one: Named): string => (one.name === null ? one.code : translate('launch.initial.names.withCode', { name: one.name, code: one.code }));

/** A decimal string the platform sends as a number; an absent or unreadable one is `null`, never a nought. Pure. */
export function decimalOf(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value === null || value === undefined || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const number = (value: string | number | null | undefined): ExportCell => {
  const parsed = decimalOf(value);
  return parsed === null ? MISSING : { kind: 'number', raw: parsed };
};
const money = (value: string | number | null | undefined, currency: string | null): ExportCell => {
  const parsed = decimalOf(value);
  if (parsed === null) return MISSING;
  return currency === null ? { kind: 'number', raw: parsed } : { kind: 'money', raw: parsed, currency };
};
const percent = (value: string | null | undefined): ExportCell => {
  const parsed = decimalOf(value);
  return parsed === null ? MISSING : { kind: 'percent', raw: parsed };
};

/** A code's words in the workbook; a code this build has no words for stays as it is. */
function words(table: Table) {
  return (code: string, translate: ExportTranslate): string => {
    const key = table[code];
    return key === undefined ? code : translate(key);
  };
}

const YES_NO = words(BOOLEAN_KEY);

/** No reason words read: every reason code of a file stands as it is. */
const NO_REASONS: ReasonWords = reasonWordsFrom((key) => key, NO_REASON_LABELS);

/**
 * A column. An empty cell that means «does not apply here» (a line nobody decided, an article with no missing term) is blank in the
 * workbook — never the assortment's «Non assortito» (ADR 9041); the column's guide says what an empty cell means.
 */
function column(key: string, kind: ExportColumn['kind'], extra: Partial<ExportColumn> = {}): ExportColumn {
  const [headerKey, descriptionKey] = COLUMN_KEYS[key] ?? [key, key];
  return { key, headerKey, descriptionKey, kind, absent: { notRanged: 'blank' }, ...extra };
}

/** A money column in the run's currency, or a plain number where the run states none. */
function moneyColumn(key: string, currency: string | null, extra: Partial<ExportColumn> = {}): ExportColumn {
  return currency === null ? column(key, 'number', { decimals: 2, ...extra }) : column(key, 'money', { currency, ...extra });
}

/**
 * Pairs by size, in the order of the size run, as «40: 12 · 41: 8» (the size as the customer names it). A size the platform could not
 * name reads «Size not named» with its reason in words — never its key (ADR 9027). Pure.
 */
export function sizesText(sizes: readonly ArticleToAllocate['sizes'][number][], say: ExportTranslate = (key) => key): string {
  return [...sizes]
    .sort((a, b) => (a.size_run_position ?? Number.MAX_SAFE_INTEGER) - (b.size_run_position ?? Number.MAX_SAFE_INTEGER) || a.size_key.localeCompare(b.size_key))
    .filter((size) => size.units > 0)
    .map((size) => {
      const name = sizeName(size);
      const reasonKey = sizeAbsentReasonKey(name);
      return `${name.code ?? `${say('size.absent.label')} (${say(reasonKey ?? 'size.absent.notPublished')})`}: ${size.units}`;
    })
    .join(' · ');
}

/** Codes in words, joined by «; »; a code with no words stays as it is, in a file. */
function wordsList(codes: readonly string[], of: (code: string) => string | null): string {
  return codes.map((code) => of(code) || code).join('; ');
}

// ── provenance ───────────────────────────────────────────────────────────────

function requestOf(id: string, titleKey: string, columns: readonly ExportColumn[], rows: () => Iterable<readonly ExportCell[]>, rowCount: number,
  filters: readonly ProvenanceFilter[], extra: Partial<ExportRequest> = {}, scenario: string | null = null, asOf: string | null = null): ExportRequest {
  return {
    id,
    titleKey,
    columns,
    rows,
    rowCount,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: asOf === null ? NOT_PUBLISHED : { from: asOf, to: asOf },
      scenario: scenario === null ? NOT_PUBLISHED : scenario,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: { filters, sort: [], visibleColumns: columns.map((one) => one.key), drillPath: [], densityMode: 'compact' },
    ...extra,
  };
}

// ── the articles to allocate ─────────────────────────────────────────────────

export const ARTICLES_EXPORT_ID = 'launch.initial.articles';
export const ARTICLES_EXPORT_TITLE_KEY = 'launch.initial.articles.title';

const STATUS_KEY: Table = {
  allocable: 'launch.initial.articles.status.allocable',
  not_allocable: 'launch.initial.articles.status.notAllocable',
  not_to_allocate: 'launch.initial.articles.status.notToAllocate',
};

/** The article's status code: to allocate and allocable, to allocate and not allocable, or not to allocate. Pure. */
export function statusOf(article: Pick<ArticleToAllocate, 'to_allocate' | 'allocable'>): 'allocable' | 'not_allocable' | 'not_to_allocate' {
  if (!article.to_allocate) return 'not_to_allocate';
  return article.allocable ? 'allocable' : 'not_allocable';
}

export const ARTICLES_COLUMNS: readonly ExportColumn[] = [
  column('article_name', 'text', { freeze: true }),
  column('article_key', 'text', { freeze: true, identifies: true }),
  column('sub_category', 'text'),
  column('status', 'label', { display: words(STATUS_KEY) }),
  column('missing_terms', 'text'),
  column('selection_reasons', 'text'),
  column('dc_units', 'integer', { total: 'sum' }),
  column('committed_units', 'integer', { total: 'sum' }),
  column('available_units', 'integer', { total: 'sum' }),
  column('sizes', 'text'),
  column('stores_present', 'integer'),
  column('first_sale_date', 'date'),
  column('later_wave', 'label', { display: YES_NO }),
];

export function articleCells(article: ArticleToAllocate, translate: ExportTranslate, names: Names = NO_NAMES, reasons: ReasonWords = NO_REASONS): readonly ExportCell[] {
  return [
    named(names.article(article.article_key)),
    text(article.article_key),
    text(article.sub_category),
    label(statusOf(article)),
    article.missing_terms.length === 0 ? NOT_APPLICABLE : text(wordsList(article.missing_terms, reasons.missing)),
    article.selection_reasons.length === 0 ? NOT_APPLICABLE : text(wordsList(article.selection_reasons, reasons.selection)),
    integer(article.dc_units),
    integer(article.committed_units),
    integer(article.available_units),
    text(sizesText(article.sizes, translate)),
    integer(article.stores_present),
    date(article.first_sale_date),
    yesNo(article.is_later_wave),
  ];
}

/** The articles list as a file: the rows given, with the market's perimeter stated. Pure. */
export function articlesRequest(
  rows: readonly ArticleToAllocate[], scope: { readonly market: string | null; readonly excluded: boolean; readonly narrowed: boolean; readonly asOf: string | null; readonly wave: number | null },
  translate: ExportTranslate, names: Names = NO_NAMES, reasons: ReasonWords = NO_REASONS,
): ExportRequest {
  const filters: ProvenanceFilter[] = [
    ...(scope.market === null ? [] : [{ field: 'country_key', operator: 'equals', value: scope.market }]),
    ...(scope.wave === null ? [] : [{ field: 'wave', operator: 'equals', value: String(scope.wave) }]),
    { field: 'include_excluded', operator: 'equals', value: scope.excluded ? 'yes' : 'no' },
    ...(scope.narrowed ? [{ field: 'allocable', operator: 'equals', value: 'no' }] : []),
  ];
  return requestOf(ARTICLES_EXPORT_ID, ARTICLES_EXPORT_TITLE_KEY, ARTICLES_COLUMNS, () => rows.map((one) => articleCells(one, translate, names, reasons)), rows.length, filters, {}, null, scope.asOf);
}

/**
 * The words of the composite cells (a list of reasons, a note): the reader's language at the moment the button was pressed.
 * A column of one code (a verdict, a constraint) keeps the code for the CSV and says it in words in the workbook (`display`).
 */
const WORDS: ExportTranslate = (key, values) => i18next.t(key, (values ?? {}) as Record<string, unknown>) as string;

/** The platform's words for the reason codes of a file, read now in the reader's language; the codes stand where none comes (never a failure of the file). */
async function readReasons(): Promise<ReasonWords> {
  return reasonWordsFrom((key, values) => WORDS(key, values), await readReasonLabels(launchReadReasonLabels, i18next.language));
}

/** The governed reasons a decision carries, in the reader's language; none read: the code stands (never a failure of the file). */
async function readDispositionReasons(): Promise<DispositionReasonWords> {
  try {
    const list = await callOperation(dispositionReasonCodes, {});
    const labels = new Map(list.map((one) => [one.code, one.label.trim()] as const));
    return (code) => {
      const found = labels.get(code);
      return found === undefined || found === '' ? null : found;
    };
  } catch {
    return NO_DISPOSITION_REASONS;
  }
}

/**
 * The route's export — the articles of the market in the URL, read to the end now. With no market in the URL
 * the file says so in its provenance and holds no row: nothing is defaulted in a file. The names are the market's newest live
 * run's (the names read is the run's, and an article not yet named by it keeps its code).
 */
export async function articlesExport(given?: ExportScope): Promise<ExportRequest> {
  const search = given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const market = readMarket(search);
  const excluded = readExcluded(search);
  const narrowed = readNotAllocable(search);
  if (market === null) return articlesRequest([], { market, excluded, narrowed, asOf: null, wave: null }, WORDS);
  const rows = await collectPages(listArticlesToAllocate, { query: { country_key: market, include_excluded: excluded } });
  const kept = narrowed ? rows.filter((row) => row.to_allocate && !row.allocable) : rows;
  const [head, newest] = await Promise.all([
    callOperation(listArticlesToAllocate, { query: { country_key: market, include_excluded: excluded, limit: 1 } }),
    callOperation(launchListRuns, { query: { country_key: market, limit: 1 } }).then((found) => found.items[0]?.run_id ?? null, () => null),
  ]);
  const names = newest === null ? NO_NAMES : await readNames(newest, [], kept.map((row) => row.article_key));
  return articlesRequest(kept, { market, excluded, narrowed, asOf: head.as_of, wave: head.wave }, WORDS, names, await readReasons());
}

// ── the run's workbook ───────────────────────────────────────────────────────

export const WORKBOOK_SHEETS = ['articles', 'scenarios', 'lines', 'cells'] as const;
export type SheetId = (typeof WORKBOOK_SHEETS)[number];

export const SHEET_TITLE_KEY: Readonly<Record<SheetId | 'parameters', string>> = {
  articles: 'launch.initial.file.sheet.articles',
  scenarios: 'launch.initial.file.sheet.scenarios',
  lines: 'launch.initial.file.sheet.lines',
  cells: 'launch.initial.file.sheet.cells',
  parameters: 'launch.initial.file.sheet.parameters',
};

/** The export ids the three run pages register. */
export const RUN_EXPORT_IDS = {
  runs: 'launch.initial.runs.workbook',
  lines: 'launch.initial.lines.workbook',
  approvals: 'launch.initial.approvals.workbook',
} as const;
export const RUN_EXPORT_TITLE_KEYS = {
  runs: 'launch.initial.runs.workbookTitle',
  lines: 'launch.initial.lines.workbookTitle',
  approvals: 'launch.initial.approvals.workbookTitle',
} as const;

const HOLD_PERCENT_DIGITS = 0;

/** The reads a workbook is built from: made now, a button was pressed. */
export interface RunFileReads {
  readonly view: RunView;
  readonly run: RunSummary;
  /** The platform's order of the sheets (its description of the workbook). */
  readonly order: readonly SheetId[];
  readonly articles: readonly RunArticle[];
  readonly scenarios: readonly RunScenario[];
  readonly lines: readonly LineRow[];
  readonly cells: readonly RunCell[];
  readonly names: Names;
  /** The platform's words for the reason codes of the file; absent: every code stands as it is. */
  readonly reasons?: ReasonWords;
  /** The governed reasons a decision carries, in words; absent: the code stands. */
  readonly dispositionReasons?: DispositionReasonWords;
}

/** The run's provenance, the same on every row (ADR 9120). Pure. */
function provenanceColumns(): readonly ExportColumn[] {
  return [
    column('run_id', 'text', { provenance: true }),
    column('wave', 'integer', { provenance: true }),
    column('as_of', 'date', { provenance: true }),
    column('commit_sha', 'text', { provenance: true }),
    column('parameter_set_id', 'text', { provenance: true }),
    column('metrics_version', 'text', { provenance: true }),
    column('model_version', 'text', { provenance: true }),
  ];
}

function provenanceCells(run: RunSummary): readonly ExportCell[] {
  return [text(run.run_id), integer(run.wave), date(run.as_of), text(run.commit_sha), text(run.parameter_set_id), text(run.metrics_version), text(run.model_version)];
}

function runArticleColumns(currency: string | null): readonly ExportColumn[] {
  return [
    column('article_name', 'text', { freeze: true }),
    column('article_key', 'text', { freeze: true, identifies: true }),
    column('outcome', 'label', { freeze: true, display: words(OUTCOME_KEY) }),
    column('reasons', 'text'),
    column('sub_category', 'text'),
    column('category', 'text'),
    column('first_sale_date', 'date'),
    column('stores_present', 'integer'),
    column('dc_units', 'integer', { total: 'sum' }),
    column('committed_units', 'integer', { total: 'sum' }),
    column('available_units', 'integer', { total: 'sum' }),
    column('reserved_units', 'integer', { total: 'sum' }),
    column('allocated_units', 'integer', { total: 'sum' }),
    column('held_nominal_units', 'integer', { total: 'sum' }),
    column('held_effective_units', 'integer', { total: 'sum' }),
    column('indifferent', 'label', { display: YES_NO }),
    column('chosen_scenario', 'text'),
    moneyColumn('list_price', currency),
    column('list_price_basis', 'label', { display: words(PRICE_BASIS_KEY) }),
    moneyColumn('landed_cost', currency),
    column('cost_provenance', 'text'),
    column('bought_units', 'integer'),
    column('store_buy_units', 'integer'),
    column('similarity_basis', 'label', { display: words(SIMILARITY_BASIS_KEY) }),
    column('history_weeks', 'integer'),
    column('elapsed_weeks', 'integer'),
    column('similar_articles', 'text'),
    column('notes', 'text'),
    ...provenanceColumns(),
  ];
}

export function runArticleCells(article: RunArticle, run: RunSummary, scenarios: readonly RunScenario[], translate: ExportTranslate, names: Names = NO_NAMES, reasons: ReasonWords = NO_REASONS): readonly ExportCell[] {
  const currency = article.currency_code ?? run.currency_code ?? null;
  const reasonOf = article.outcome === 'not_selected' ? reasons.selection : reasons.missing;
  const chosen = scenarios.find((one) => one.scenario_id === article.chosen_scenario_id);
  return [
    named(names.article(article.article_key)),
    text(article.article_key),
    label(article.outcome),
    article.reasons.length === 0 ? NOT_APPLICABLE : text(wordsList(article.reasons, reasonOf)),
    text(article.sub_category),
    text(article.category),
    date(article.first_sale_date),
    integer(article.stores_present),
    integer(article.dc_units),
    integer(article.committed_units),
    integer(article.available_units),
    integer(article.reserved_units),
    integer(article.allocated_units),
    integer(article.held_nominal_units),
    integer(article.held_effective_units),
    yesNo(article.indifferent),
    chosen === undefined ? MISSING : percent(chosen.hold_fraction),
    money(article.list_price, currency),
    label(article.list_price_basis),
    money(article.landed_cost, currency),
    text(article.cost_provenance),
    integer(article.bought_units),
    integer(article.store_buy_units),
    label(article.similarity_basis),
    integer(article.history_weeks),
    integer(article.elapsed_weeks),
    article.similar_articles === null || article.similar_articles === undefined ? MISSING : text(article.similar_articles.map((key) => nameWithCode(translate, names.article(key))).join('; ')),
    article.notes.length === 0 ? NOT_APPLICABLE : text(article.notes.map((note) => reasons.note(note) || note).join('; ')),
    ...provenanceCells(run),
  ];
}

export function scenarioColumns(currency: string | null): readonly ExportColumn[] {
  return [
    column('article_name', 'text', { freeze: true }),
    column('article_key', 'text', { freeze: true, identifies: true }),
    column('scenario', 'text', { freeze: true }),
    column('scenario_id', 'text', { technical: true }),
    column('hold_fraction', 'percent', { decimals: HOLD_PERCENT_DIGITS }),
    column('nominal_hold_units', 'integer'),
    column('effective_hold_units', 'integer'),
    column('push_units', 'integer'),
    column('expected_full_price_units', 'number', { decimals: 1 }),
    column('expected_terminal_units', 'number', { decimals: 1 }),
    moneyColumn('realised_margin', currency, { polarity: 'higher-is-better' }),
    column('chosen', 'label', { display: YES_NO }),
    column('identical_to', 'text'),
  ];
}

/** A scenario in words — «Hold back 20%» — from its hold fraction; the platform's id only where the fraction cannot be read. */
function scenarioWords(translate: ExportTranslate, scenario: Pick<RunScenario, 'hold_fraction' | 'scenario_id'>): string {
  const fraction = decimalOf(scenario.hold_fraction);
  return fraction === null ? scenario.scenario_id : translate('launch.initial.scenarios.holdOf', { percent: formatPercent(fraction, i18next.language, 0) });
}

export function scenarioCells(scenario: RunScenario, currency: string | null, names: Names, translate: ExportTranslate = WORDS, siblings: readonly RunScenario[] = []): readonly ExportCell[] {
  return [
    named(names.article(scenario.article_key)),
    text(scenario.article_key),
    text(scenarioWords(translate, scenario)),
    text(scenario.scenario_id),
    percent(scenario.hold_fraction),
    integer(scenario.nominal_hold_units),
    integer(scenario.effective_hold_units),
    integer(scenario.push_units),
    number(scenario.expected_full_price_units),
    number(scenario.expected_terminal_units),
    money(scenario.realised_margin, currency),
    yesNo(scenario.chosen),
    scenario.identical_to.length === 0 ? NOT_APPLICABLE : text(scenario.identical_to.map((id) => {
      const other = siblings.find((one) => one.scenario_id === id && one.article_key === scenario.article_key);
      return other === undefined ? id : scenarioWords(translate, other);
    }).join('; ')),
  ];
}

/** The words of the governed reasons a decision carries; a code the list does not hold stands as it is. */
export type DispositionReasonWords = (code: string) => string | null;
const NO_DISPOSITION_REASONS: DispositionReasonWords = () => null;

/** «approved» as «Approved»: the decision's words start a cell. Pure. */
const sentenceCase = (word: string): string => (word === '' ? word : `${word.charAt(0).toUpperCase()}${word.slice(1)}`);

/** A ship week as the Monday it starts on; a week that is not an ISO week stands as the platform wrote it. Pure. */
const weekCell = (week: string | null | undefined): ExportCell => {
  const start = isoWeekStart(week);
  return start === null ? text(week) : { kind: 'date', iso: start };
};

/** The lines' columns: the decision file — each line, what bound it, who decided it and the run it came from (ADR 9120). */
export function lineColumns(currency: string | null, reasons: ReasonWords = NO_REASONS, dispositionReasons: DispositionReasonWords = NO_DISPOSITION_REASONS): ExportColumn[] {
  return [
    column('article_name', 'text', { freeze: true }),
    column('article_key', 'text', { freeze: true }),
    column('store_name', 'text', { freeze: true }),
    column('store_key', 'text', { freeze: true }),
    // The size as the customer names it, with its scale; the key is an identifier on the «Technical» sheet (ADR 9027).
    ...SIZE_EXPORT_COLUMNS,
    column('units', 'integer', { total: 'sum' }),
    column('ship_week', 'date'),
    column('line_reason', 'label', { display: (code) => reasons.line(code) || code }),
    moneyColumn('expected_impact', currency, { polarity: 'higher-is-better' }),
    column('share', 'number', { decimals: 4 }),
    column('position_units', 'integer'),
    column('core_units', 'integer'),
    column('quantile_units', 'integer'),
    column('need_units', 'integer'),
    column('disposition', 'label', { display: (code, translate) => sentenceCase(words(DISPOSITION_KEY)(code, translate)) }),
    column('disposition_reason', 'label', { display: (code) => dispositionReasons(code) || code }),
    column('adjusted_units', 'integer'),
    column('decided_by', 'label', { display: PERSON_NAME_DISPLAY }),
    // The market's own day of the decision, as the platform tells it from the market's time zone; empty with its reason where it cannot.
    column('decided_at', 'date'),
    column('decided_on_reason', 'label', { display: (code, translate) => reasons.marketDay(code) || translate('launch.initial.unclassified') }),
    // The two facts the client asked the file to carry (DEC-127): the day the line stops holding pairs at the DC, and whether
    // the store's shipment stays under the minimum — flagged, never dropped. Each has the platform's reason where it states none.
    column('valid_until', 'date'),
    column('valid_until_reason', 'label', { display: (code, translate) => reasons.validUntil(code) || translate('launch.initial.unclassified') }),
    column('shipment_units', 'integer'),
    column('shipment_minimum_units', 'integer'),
    column('below_shipment_minimum', 'label', { display: YES_NO }),
    column('shipment_reason', 'label', { display: (code, translate) => reasons.shipment(code) || translate('launch.initial.unclassified') }),
    ...provenanceColumns(),
  ];
}

export function lineCells(line: LineRow, run: RunSummary, names: Names = NO_NAMES): readonly ExportCell[] {
  const currency = line.currency_code || run.currency_code || null;
  const applies = <T,>(value: T | null, cell: (one: T) => ExportCell): ExportCell => (value === null ? NOT_APPLICABLE : cell(value));
  const size = sizeName({
    size_key: line.size_key, size_code: line.size_code, size_scale: line.size_scale ?? null, size_run_position: line.size_run_position ?? null, size_absent_reason: line.size_absent_reason ?? null,
  });
  return [
    named(names.article(line.article_key)),
    text(line.article_key),
    named(names.store(line.store_id)),
    text(line.store_id),
    ...sizeExportCells(size),
    integer(line.units),
    weekCell(line.ship_week),
    label(line.reason_code),
    money(line.expected_impact, currency),
    number(line.share),
    integer(line.position_units),
    integer(line.core_units),
    integer(line.quantile_units),
    integer(line.need_units),
    applies(line.disposition, (value) => label(value)),
    applies(line.disposition_reason_code, (value) => label(value)),
    applies(line.adjusted_units, (value) => integer(value)),
    decidedByExportCell({
      disposition: line.disposition,
      actor: line.disposition_actor,
      actorName: line.disposition_actor_name,
      actorNameAbsentReason: line.disposition_actor_name_absent_reason,
    }),
    applies(line.decided_on_market_day ?? null, (value) => date(value)),
    line.disposition === null || line.decided_on_market_day_absent_reason === null || line.decided_on_market_day_absent_reason === undefined ? NOT_APPLICABLE : label(line.decided_on_market_day_absent_reason),
    applies(line.valid_until, (value) => date(value)),
    label(line.valid_until_absent_reason),
    applies(line.shipment_units, (value) => integer(value)),
    applies(line.shipment_minimum_units, (value) => integer(value)),
    applies(line.below_shipment_minimum, (value) => yesNo(value)),
    label(line.shipment_absent_reason),
    ...provenanceCells(run),
  ];
}

export function cellColumns(reasons: ReasonWords): readonly ExportColumn[] {
  return [
  column('article_name', 'text', { freeze: true }),
  column('article_key', 'text', { freeze: true }),
  column('store_name', 'text', { freeze: true }),
  column('store_key', 'text', { freeze: true }),
  column('cell_outcome', 'label', { display: words(CELL_OUTCOME_KEY) }),
  column('cell_reason', 'label', { display: (code) => reasons.cell(code) || code }),
  column('units', 'integer', { total: 'sum' }),
  column('share', 'number', { decimals: 4 }),
  column('mean_units', 'number', { decimals: 2 }),
  column('window_mean_units', 'number', { decimals: 2 }),
  column('dispersion', 'number', { decimals: 4 }),
  column('dispersion_observed', 'label', { display: YES_NO }),
  column('position_units', 'integer'),
  column('quantile_units', 'integer'),
  column('core_units', 'integer'),
  column('need_units', 'integer'),
  column('horizon_units', 'integer'),
  column('horizon_days', 'integer'),
  column('capacity_measured', 'label', { display: YES_NO }),
  column('presentation_minimum_units', 'integer'),
  ];
}

export function cellCells(cell: RunCell, names: Names): readonly ExportCell[] {
  return [
    named(names.article(cell.article_key)),
    text(cell.article_key),
    named(names.store(cell.store_key)),
    text(cell.store_key),
    label(cell.outcome),
    cell.reason_code === null || cell.reason_code === undefined ? NOT_APPLICABLE : label(cell.reason_code),
    integer(cell.units),
    number(cell.share),
    number(cell.mean_units),
    number(cell.window_mean_units),
    number(cell.dispersion),
    yesNo(cell.dispersion_observed),
    integer(cell.position_units),
    integer(cell.quantile_units),
    integer(cell.core_units),
    integer(cell.need_units),
    integer(cell.horizon_units),
    integer(cell.horizon_days),
    yesNo(cell.capacity_measured),
    integer(cell.presentation_minimum_units),
  ];
}

export const PARAMETER_COLUMNS: readonly ExportColumn[] = [
  column('parameter', 'label', { freeze: true, identifies: true, display: (code, translate) => paramName((key, values) => translate(key, values), code) }),
  column('parameter_value', 'text'),
  column('parameter_scope', 'text'),
  column('parameter_set_id', 'text', { technical: true }),
  column('valid_from', 'date'),
];

/** The parameters in force, a value in the unit the planner reads it in (a share as a percentage, a count with its unit) and a scope in words. Pure. */
export function parameterRows(view: RunView, translate: ExportTranslate): readonly (readonly ExportCell[])[] {
  const t = translatorOf((key, values) => translate(key, values));
  const language = i18next.language;
  return (view.parameters ?? []).map((one) => [
    label(one.parameter_key),
    one.value === null || one.value === undefined ? MISSING : text(parameterValueWords(t, one.parameter_key, one.value, language)),
    text(scopeWords(t, one.scope, language)),
    text(one.parameter_set_id),
    date(one.valid_from),
  ]);
}

/** The run's workbook as an export request: the sheets in the platform's order, the page's own first, then the parameters in force. Pure. */
export function runWorkbookRequest(
  reads: RunFileReads, first: SheetId, id: string, titleKey: string, translate: ExportTranslate = WORDS, narrowing: readonly ProvenanceFilter[] = [],
): ExportRequest {
  const { run, view, names } = reads;
  const reasons = reads.reasons ?? NO_REASONS;
  const currency = run.currency_code ?? null;
  const built: Record<SheetId, WorkbookSheet> = {
    articles: { titleKey: SHEET_TITLE_KEY.articles, columns: runArticleColumns(currency), rows: () => reads.articles.map((one) => runArticleCells(one, run, reads.scenarios.filter((s) => s.article_key === one.article_key), translate, names, reasons)) },
    scenarios: { titleKey: SHEET_TITLE_KEY.scenarios, columns: scenarioColumns(currency), rows: () => reads.scenarios.map((one) => scenarioCells(one, currency, names, translate, reads.scenarios)) },
    lines: { titleKey: SHEET_TITLE_KEY.lines, columns: lineColumns(currency, reasons, reads.dispositionReasons), rows: () => reads.lines.map((one) => lineCells(one, run, names)) },
    cells: { titleKey: SHEET_TITLE_KEY.cells, columns: cellColumns(reasons), rows: () => reads.cells.map((one) => cellCells(one, names)) },
  };
  const order: readonly SheetId[] = [first, ...reads.order.filter((one) => one !== first)];
  const sheets: WorkbookSheet[] = [
    ...order.map((one) => built[one]),
    { titleKey: SHEET_TITLE_KEY.parameters, columns: PARAMETER_COLUMNS, rows: () => parameterRows(view, translate) },
  ];
  const head = built[first];
  const filters: readonly ProvenanceFilter[] = [
    { field: 'run_id', operator: 'equals', value: run.run_id },
    { field: 'country_key', operator: 'equals', value: run.country_key },
    { field: 'wave', operator: 'equals', value: String(run.wave) },
    ...narrowing,
  ];
  const count = { articles: reads.articles.length, scenarios: reads.scenarios.length, lines: reads.lines.length, cells: reads.cells.length }[first];
  return requestOf(id, titleKey, head.columns, head.rows as () => Iterable<readonly ExportCell[]>, count, filters, { workbook: { sheets } }, run.simulation_id ?? null, run.as_of);
}

/** The sheets of the platform's description of the workbook that this build knows, in its order; every sheet if it cannot be read. Pure. */
export function sheetOrder(descriptor: { readonly sheets: readonly { readonly sheet: string }[] } | null): readonly SheetId[] {
  if (descriptor === null) return WORKBOOK_SHEETS;
  const known = new Set<string>(WORKBOOK_SHEETS);
  const listed = descriptor.sheets.map((one) => one.sheet).filter((one): one is SheetId => known.has(one));
  return listed.length === 0 ? WORKBOOK_SHEETS : listed;
}

/**
 * Every read the file needs, made now — a button was pressed. Each list is ONE streamed request, and they start together; the
 * names of the keys the lists hold are asked for once they have arrived. `only` reads one list alone (the CSV is the first
 * sheet's table and needs no other), and `onProgress` hears its rows as they arrive.
 */
export async function readRunFile(runId: string, only: SheetId | null = null, onProgress?: CsvProgress): Promise<RunFileReads> {
  const [view, descriptor] = await Promise.all([
    callOperation(launchReadRun, { path: { run_id: runId } }),
    callOperation(readRunWorkbook, { path: { run_id: runId } }).catch(() => null),
  ]);
  if (view.run === null || view.run === undefined) throw new Error('The run is not written yet.');
  const run = view.run;
  const order = sheetOrder(descriptor);
  const wants = (kind: SheetId): boolean => only === null || only === kind;
  const progress = (kind: SheetId): CsvProgress | undefined => (only === kind ? onProgress : undefined);
  const [articles, scenarios, lines, cells] = await Promise.all([
    wants('articles') ? readList(runId, 'articles', ARTICLES_LIST, progress('articles')) : Promise.resolve([] as RunArticle[]),
    wants('scenarios') ? readList(runId, 'scenarios', SCENARIOS_LIST, progress('scenarios')) : Promise.resolve([] as RunScenario[]),
    wants('lines') ? readList(runId, 'lines', LINES_LIST, progress('lines')) : Promise.resolve([] as LineRow[]),
    wants('cells') ? readList(runId, 'cells', CELLS_LIST, progress('cells')) : Promise.resolve([] as RunCell[]),
  ]);
  const names = await readNames(
    runId,
    [...lines.map((one) => one.store_id), ...cells.map((one) => one.store_key)],
    [...articles.map((one) => one.article_key), ...scenarios.map((one) => one.article_key), ...lines.map((one) => one.article_key), ...cells.map((one) => one.article_key)],
  );
  return { view, run, order, articles, scenarios, lines, cells, names, reasons: await readReasons(), dispositionReasons: await readDispositionReasons() };
}

// ── the approved lines ───────────────────────────────────────────────────────

/** The two decisions that stand behind a line: it is sent. A rejected or deferred line is not in the approvals file. */
export const APPROVED_DISPOSITIONS: readonly LineDisposition[] = ['approved', 'adjusted'];

/**
 * The reads of the approvals file: the run's header and parameters, and ONLY the lines somebody approved or adjusted — one streamed request
 * of the platform's lines file, narrowed by it (`disposition=approved&disposition=adjusted`), whose row count follows the narrowing. A
 * rejected, deferred or undecided line is not in the file. Made now: a button was pressed. `onProgress` hears the lines as they arrive.
 */
export async function readApprovedFile(runId: string, onProgress?: CsvProgress): Promise<RunFileReads> {
  const view = await callOperation(launchReadRun, { path: { run_id: runId } });
  if (view.run === null || view.run === undefined) throw new Error('The run is not written yet.');
  const run = view.run;
  const lines = await readList(runId, 'lines', LINES_LIST, onProgress, APPROVED_DISPOSITIONS);
  const names = await readNames(runId, lines.map((one) => one.store_id), lines.map((one) => one.article_key));
  return {
    view, run, order: ['lines'], articles: [], scenarios: [], lines, cells: [], names, reasons: await readReasons(), dispositionReasons: await readDispositionReasons(),
  };
}

/** The approvals file: the approved lines first, then the parameters in force. The perimeter states which decisions it holds. Pure. */
export function approvedWorkbookRequest(reads: RunFileReads, id: string, titleKey: string, translate: ExportTranslate = WORDS): ExportRequest {
  return runWorkbookRequest(reads, 'lines', id, titleKey, translate, [{ field: 'disposition', operator: 'in', value: APPROVED_DISPOSITIONS.join(', ') }]);
}

const NO_RUN: RunSummary = {
  allocated_units: 0, articles_allocated: 0, articles_not_allocable: 0, articles_to_allocate: 0, articles_with_stock: 0, as_of: '', commit_sha: '',
  country_key: '', dc_units: 0, held_units: 0, metrics_version: '', missing_inputs: [], model_version: '', parameter_set_id: '', recorded_at: '',
  reserved_units: 0, run_id: '', status: '', stores_left_out: 0, stores_served: 0, distinct_stores_left_out: 0, distinct_stores_served: 0, wave: 0,
};

// ── the tables a run page draws besides its articles and lines ───────────────

/** The export ids of the run page's other tables: the cells, the hold-back of an opened article, the parameters in force. */
export const CELLS_EXPORT_ID = 'launch.initial.cells';
export const SCENARIOS_EXPORT_ID = 'launch.initial.scenarios';
export const PARAMETERS_EXPORT_ID = 'launch.initial.parameters';

/** The run's provenance written on every row of a table's own file (ADR 9120). */
const ownProvenance = (columns: readonly ExportColumn[]): readonly ExportColumn[] => [...columns, ...provenanceColumns()];

/** The run in the URL, read; `null` for an address that names none. */
async function runOfAddress(given: ExportScope | undefined): Promise<{ readonly search: string; readonly runId: string | null }> {
  const search = given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  return { search, runId: readRunId(search) };
}

/**
 * The cells of the run as a file: the whole list the table shows a page of, narrowed as the table is (the outcomes, the article) — a table
 * that reads fifty at a time still leaves with every row of its set (ADR 9048). `outcomes` empty: every cell of the run (the cells report).
 * Pure.
 */
export function cellsRequest(
  run: RunSummary, cells: readonly RunCell[], names: Names, reasons: ReasonWords, titleKey: string, narrowing: readonly ProvenanceFilter[],
): ExportRequest {
  const filters: readonly ProvenanceFilter[] = [{ field: 'run_id', operator: 'equals', value: run.run_id }, { field: 'country_key', operator: 'equals', value: run.country_key }, ...narrowing];
  return requestOf(
    CELLS_EXPORT_ID, titleKey, ownProvenance(cellColumns(reasons)), () => cells.map((one) => [...cellCells(one, names), ...provenanceCells(run)]), cells.length, filters, {},
    run.simulation_id ?? null, run.as_of,
  );
}

/** Reads the cells a table shows, all of them (one outcome at a time, as the platform lists them), and writes the file. */
export async function readCellsFile(run: RunSummary, article: string | null, outcomes: readonly string[], titleKey: string): Promise<ExportRequest> {
  const lists = await Promise.all(outcomes.map((outcome) => collectPages(listRunCells, {
    path: { run_id: run.run_id }, query: { outcome, ...(article === null ? {} : { article_key: article }) },
  })));
  const cells = lists.flat();
  const names = await readNames(run.run_id, cells.map((one) => one.store_key), cells.map((one) => one.article_key));
  const narrowing: ProvenanceFilter[] = [
    ...(article === null ? [] : [{ field: 'article_key', operator: 'equals', value: article }]),
    ...(outcomes.length === 0 ? [] : [{ field: 'outcome', operator: 'in', value: outcomes.join(', ') }]),
  ];
  return cellsRequest(run, cells, names, await readReasons(), titleKey, narrowing);
}

const CELLS_TITLE = { served: 'launch.initial.runs.cellsServedTitle', leftOut: 'launch.initial.runs.cellsLeftOutTitle', article: 'launch.initial.cells.title' } as const;
const SERVED_OUTCOMES = ['served'] as const;
const LEFT_OUT_OUTCOMES = ['excluded', 'not_served'] as const;

/**
 * The cells table of the runs page: the list a card opened (`?view=`: sends made, or not made) or the stores that got nothing of the opened
 * article (`?article=`), whole. With neither in the address the file is the run's whole cells report.
 */
export async function cellsExport(given?: ExportScope): Promise<ExportRequest> {
  const { search, runId } = await runOfAddress(given);
  if (runId === null) return cellsRequest(NO_RUN, [], NO_NAMES, NO_REASONS, CELLS_TITLE.served, []);
  const run = (await callOperation(launchReadRun, { path: { run_id: runId } })).run;
  if (run === null || run === undefined) throw new Error('The run is not written yet.');
  const view = readCells(search);
  const article = readOpenArticle(search);
  if (view === null && article === null) {
    const cells = await readList(runId, 'cells', CELLS_LIST);
    return cellsRequest(run, cells, await readNames(runId, cells.map((one) => one.store_key), cells.map((one) => one.article_key)), await readReasons(), CELLS_TITLE.served, []);
  }
  if (view !== null) return readCellsFile(run, null, view === 'served' ? SERVED_OUTCOMES : LEFT_OUT_OUTCOMES, view === 'served' ? CELLS_TITLE.served : CELLS_TITLE.leftOut);
  return readCellsFile(run, article, LEFT_OUT_OUTCOMES, CELLS_TITLE.article);
}

/** The hold-back scenarios of one article as a file of their own (the table under the opened article). Pure. */
export function scenariosRequest(run: RunSummary, article: string | null, scenarios: readonly RunScenario[], names: Names, translate: ExportTranslate = WORDS): ExportRequest {
  const currency = run.currency_code ?? null;
  const filters: readonly ProvenanceFilter[] = [
    { field: 'run_id', operator: 'equals', value: run.run_id }, { field: 'country_key', operator: 'equals', value: run.country_key },
    ...(article === null ? [] : [{ field: 'article_key', operator: 'equals', value: article }]),
  ];
  return requestOf(
    SCENARIOS_EXPORT_ID, SHEET_TITLE_KEY.scenarios, ownProvenance(scenarioColumns(currency)),
    () => scenarios.map((one) => [...scenarioCells(one, currency, names, translate, scenarios), ...provenanceCells(run)]), scenarios.length, filters, {},
    run.simulation_id ?? null, run.as_of,
  );
}

/** The opened article's hold-back (`?article=`); with none opened the file holds no row and says so in its perimeter. */
export async function scenariosExport(given?: ExportScope): Promise<ExportRequest> {
  const { search, runId } = await runOfAddress(given);
  const article = readOpenArticle(search);
  if (runId === null || article === null) return scenariosRequest(NO_RUN, article, [], NO_NAMES);
  const [view, read] = await Promise.all([
    callOperation(launchReadRun, { path: { run_id: runId } }),
    callOperation(readArticleScenarios, { path: { run_id: runId, article_key: article } }).catch(() => null),
  ]);
  if (view.run === null || view.run === undefined) throw new Error('The run is not written yet.');
  return scenariosRequest(view.run, article, read?.scenarios ?? [], await readNames(runId, [], [article]));
}

/** The parameters in force when the run read them, as a file of their own (the table at the foot of the runs page). Pure. */
export function parametersRequest(view: RunView, run: RunSummary, translate: ExportTranslate = WORDS): ExportRequest {
  const rows = parameterRows(view, translate);
  const filters: readonly ProvenanceFilter[] = [{ field: 'run_id', operator: 'equals', value: run.run_id }, { field: 'country_key', operator: 'equals', value: run.country_key }];
  return requestOf(
    PARAMETERS_EXPORT_ID, SHEET_TITLE_KEY.parameters, ownProvenance(PARAMETER_COLUMNS), () => rows.map((row) => [...row, ...provenanceCells(run)]), rows.length, filters, {},
    run.simulation_id ?? null, run.as_of,
  );
}

export async function parametersExport(given?: ExportScope): Promise<ExportRequest> {
  const { runId } = await runOfAddress(given);
  if (runId === null) return parametersRequest({ run_id: '', state: 'none', parameters: [], provenance: {} }, NO_RUN);
  const view = await callOperation(launchReadRun, { path: { run_id: runId } });
  if (view.run === null || view.run === undefined) throw new Error('The run is not written yet.');
  return parametersRequest(view, view.run);
}

/** The export id of the similar articles a line's split was read from. */
export const SIMILAR_EXPORT_ID = 'launch.initial.similar';
export const SIMILAR_TITLE_KEY = 'launch.initial.evidence.similarTitle';

export function similarColumns(reasons: ReasonWords): readonly ExportColumn[] {
  return ownProvenance([
    column('article_name', 'text', { freeze: true }),
    column('article_key', 'text', { freeze: true, identifies: true }),
    column('similarity_basis', 'label', { display: words(SIMILARITY_BASIS_KEY) }),
    column('units_at_store', 'integer'),
    column('total_units', 'integer'),
    column('similar_share', 'percent', { decimals: 1 }),
    column('similar_share_reason', 'label', { display: (code, translate) => reasons.similarShare(code) || translate('launch.initial.unclassified') }),
  ]);
}

/** The similar articles of one line as a file: each with its sales at the store and in all, and the share the split took from them. Pure. */
export function similarRequest(run: RunSummary, proposal: string | null, similar: readonly SimilarArticleShare[], reasons: ReasonWords = NO_REASONS): ExportRequest {
  const filters: readonly ProvenanceFilter[] = [
    { field: 'run_id', operator: 'equals', value: run.run_id }, { field: 'country_key', operator: 'equals', value: run.country_key },
    ...(proposal === null ? [] : [{ field: 'line', operator: 'equals', value: proposal }]),
  ];
  const rows = (): Iterable<readonly ExportCell[]> => similar.map((one) => [
    one.article_name === null || one.article_name === undefined || one.article_name === '' ? MISSING : text(one.article_name),
    text(one.article_key),
    label(one.basis),
    integer(one.units_at_store),
    integer(one.total_units),
    percent(one.share),
    label(one.share_absent_reason),
    ...provenanceCells(run),
  ]);
  return requestOf(SIMILAR_EXPORT_ID, SIMILAR_TITLE_KEY, similarColumns(reasons), rows, similar.length, filters, {}, run.simulation_id ?? null, run.as_of);
}

/** The similar articles of the line in the address (`?row=`); with none chosen the file holds no row and says so. */
export async function similarExport(given?: ExportScope): Promise<ExportRequest> {
  const { search, runId } = await runOfAddress(given);
  const row = readRowParam(search);
  if (runId === null || row === null) return similarRequest(NO_RUN, row, []);
  const [why, view] = await Promise.all([
    callOperation(launchReadLineWhy, { path: { run_id: runId, proposal_id: row } }),
    callOperation(launchReadRun, { path: { run_id: runId } }),
  ]);
  if (view.run === null || view.run === undefined) throw new Error('The run is not written yet.');
  return similarRequest(view.run, row, why.similar ?? [], await readReasons());
}

/** A route's export of the run in the URL. With no run in the URL the file says so in its provenance and holds no row. */
export function runWorkbookExport(first: SheetId, id: string, titleKey: string, approved = false) {
  return async (given?: ExportScope): Promise<ExportRequest> => {
    const runId = readRunId(given?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
    if (runId === null) {
      const empty: RunFileReads = { view: { run_id: '', state: 'none', parameters: [], provenance: {} }, run: NO_RUN, order: approved ? ['lines'] : WORKBOOK_SHEETS, articles: [], scenarios: [], lines: [], cells: [], names: NO_NAMES };
      return approved ? approvedWorkbookRequest(empty, id, titleKey) : runWorkbookRequest(empty, first, id, titleKey);
    }
    return approved ? approvedWorkbookRequest(await readApprovedFile(runId), id, titleKey) : runWorkbookRequest(await readRunFile(runId), first, id, titleKey);
  };
}

export const runsExport = runWorkbookExport('articles', RUN_EXPORT_IDS.runs, RUN_EXPORT_TITLE_KEYS.runs);
export const linesExport = runWorkbookExport('lines', RUN_EXPORT_IDS.lines, RUN_EXPORT_TITLE_KEYS.lines);
/** The approvals page's file holds the lines somebody approved or adjusted, and no others (its title says so). */
export const approvalsExport = runWorkbookExport('lines', RUN_EXPORT_IDS.approvals, RUN_EXPORT_TITLE_KEYS.approvals, true);
